import { motion } from 'framer-motion';
import { t } from 'i18next';
import { findIndex, first, isEmpty, sortBy } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import StickyHeader from '../presentation/components/bar/StickyHeader';
import LeaveChatRoomDialog from '../components/alerts/LeaveChatRoomDialog';
import leaveChatRoom from '../components/api/mutations/legacy/leaveChatRoom';
import ChatMessageBubble from '../components/chats/ChatMessageBubble';
import DMMessageInputBox from '../components/chats/DMMessageInputBox';
import NewMessageAlertButton from '../components/chats/NewMessageAlertButton';
import VerifiedIcon from '../components/icons/VerifiedIcon';
import { useNewMessageDetection } from '../hooks/useNewMessageDetection';
import { useMessageListScroll } from '../hooks/useMessageListScroll';
import { useChatRoom } from '../presentation/hooks/useChatRoom';
import type { DMChatMessage, DMChatRoom } from '../types/dm';
import { ellipsis } from '../utils/TextUtil';
import { openNewTab, refreshScreenSize } from '../utils/UIUtils';

interface DMUserProfile {
  userId: string;
  userProfileId: string;
  nickname: string;
  identified: boolean;
  thumbnailUrl?: string;
  pictureUrl?: string;
}

interface ChatRoomLayoutProps {
  header: React.ReactNode;
  messages: React.ReactNode;
  input: React.ReactNode;
}

function ChatRoomLayout({ header, messages, input }: ChatRoomLayoutProps): JSX.Element {
  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {header}
      <div className="flex-1 bg-background-light dark:bg-background-dark pt-[calc(59px+env(safe-area-inset-top))] overflow-hidden">
        <div className="border-b border-divider-light dark:border-divider-dark -mx-4" />
        <div className="h-full overflow-hidden pb-[55px]">{messages}</div>
      </div>
      {input}
    </div>
  );
}

interface ChatRoomHeaderProps {
  chatRoom?: DMChatRoom | null;
  connectionStatus: any;
  onOpenMenu: () => void;
}

function ChatRoomHeader({
  chatRoom,
  connectionStatus,
  onOpenMenu
}: ChatRoomHeaderProps): JSX.Element {
  const dmUserProfile = useMemo(() => {
    if (!chatRoom?.userProfiles) return undefined;
    const index = findIndex(chatRoom.userProfiles, (p: any) => p.userId !== chatRoom.currentUserId);
    return chatRoom.userProfiles[index];
  }, [chatRoom]);

  const navigateUserProfile = (userProfile?: DMUserProfile) => {
    if (!userProfile) return;

    const currentPath = window.location.pathname;
    const userProfilePath = `/feed/user_profile/${userProfile.userProfileId}`;
    if (currentPath === userProfilePath) return;

    if (!isEmpty(userProfile.userProfileId)) {
      openNewTab(userProfilePath);
    }
  };

  return (
    <StickyHeader
      title={String(t('DMChatRoom.pageTitle'))}
      showBackButton={true}
      showTitle={false}
      centerContent={
        (dmUserProfile && (
          <div
            className="flex items-center justify-center space-x-1 px-3 cursor-pointer"
            onClick={() => navigateUserProfile(dmUserProfile)}
          >
            <div className="text-main-light-1 dark:text-main-dark-1 font-bold text-[17px] leading-5 text-center">
              {ellipsis(dmUserProfile.nickname || '', 20)}
            </div>
            {dmUserProfile.identified && <VerifiedIcon width={14} height={14} />}
          </div>
        )) as any
      }
      rightContent={(<MeatBallMenus onOpen={onOpenMenu} />) as any}
    />
  );
}

interface VirtualizedMessageListProps {
  messages: DMChatMessage[];
  chatRoom?: DMChatRoom | null;
  messageStatuses: Map<string, any>;
  onLoadMore: () => void;
  hasMore: boolean;
  onNewMessage?: () => void;
}

function VirtualizedMessageList({
  messages,
  chatRoom,
  messageStatuses,
  onLoadMore,
  hasMore,
  onNewMessage
}: VirtualizedMessageListProps): JSX.Element {
  const messageAreaRef = useRef<HTMLDivElement>(null);

  const myLastUnSeenMessage = useMemo(() => {
    return first(
      sortBy(
        messages?.filter((message) => message.isMine && !!message.seenAt),
        (m) => {
          return -(m.seenAt ? new Date(m.seenAt).getTime() : 0);
        }
      )
    );
  }, [messages]);

  // 공통 훅 사용
  const { showNewMessageButton, handleAtBottomStateChange, handleNewMessageButtonClick } =
    useNewMessageDetection(messages, () => {
      scrollToBottom();
      onNewMessage?.();
    });

  // 스크롤 관련 훅
  const { listContainerRef, handleScroll, scrollToBottom } = useMessageListScroll({
    messages,
    hasMore,
    onLoadMore,
    onAtBottomChange: handleAtBottomStateChange,
    enableInitialScrollToBottom: true
  });

  const noticeMessage = (): string | undefined => {
    if (chatRoom?.chatRoomUsers?.some((u: any) => !!u.deletedAt)) {
      return String(t('DMChatRoom.noticeDeletedUser'));
    }
    if (chatRoom?.chatRoomUsers?.some((u: any) => !!u.leftAt)) {
      return String(t('DMChatRoom.noticeLeftUser'));
    }
    return undefined;
  };

  return (
    <div
      ref={messageAreaRef}
      className="relative flex flex-col pb-[64px] safeAreaMarginBottom h-full overflow-hidden"
    >
      <NoticeText noticeMessage={noticeMessage()} />
      <div className="flex-1 overflow-hidden">
        <div ref={listContainerRef} className="h-full overflow-auto" onScroll={handleScroll}>
          {messages?.map((message, index) => {
            if (!message) return null;
            const previousMessage = index > 0 ? messages[index - 1] : undefined;
            const isFirstMessage = index === 0;
            return (
              <div className="px-[24px]" key={message.chatRoomMessageId}>
                <ChatMessageBubble
                  chatRoom={chatRoom!}
                  message={message}
                  previousMessage={previousMessage}
                  myLastUnSeenMessage={myLastUnSeenMessage}
                  isFirstMessage={isFirstMessage}
                  styleVariant="default"
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* 새 메시지 알림 버튼 */}
      <NewMessageAlertButton
        show={showNewMessageButton}
        onClick={handleNewMessageButtonClick}
        styleVariant="default"
      />
    </div>
  );
}

interface ChatInputProps {
  onSendMessage: (content: string, messageType?: any, imageUrls?: string[]) => Promise<void>;
  disabled: boolean;
  dmUserProfile?: DMUserProfile;
  messageAreaRef?: React.RefObject<HTMLDivElement>;
  onSentMessage?: () => void;
}

function ChatInput({
  onSendMessage,
  disabled,
  dmUserProfile,
  messageAreaRef,
  onSentMessage
}: ChatInputProps): JSX.Element {
  const noticeMessage = (): string | undefined => {
    // 여기서는 disabled로 판단
    return disabled ? String(t('DMChatRoom.noticeDeletedUser')) : undefined;
  };

  return (
    <DMMessageInputBox
      dmUserProfile={dmUserProfile}
      messageAreaRef={messageAreaRef}
      onSentMessage={onSentMessage || (() => {})}
      disabled={!isEmpty(noticeMessage())}
    />
  );
}

interface NoticeTextProps {
  noticeMessage?: string;
}

function NoticeText({ noticeMessage }: NoticeTextProps): JSX.Element | null {
  if (isEmpty(noticeMessage)) return null;

  return (
    <div className="w-full pt-5 text-center">
      <div className="text-main-light-3 dark:text-main-dark-3 text-xs font-normal">
        {noticeMessage}
      </div>
    </div>
  );
}

interface MeatBallMenusProps {
  onOpen: () => void;
}

function MeatBallMenus({ onOpen }: MeatBallMenusProps): JSX.Element {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="relative">
      <button
        className="text-lg font-semibold text-main-light-1 dark:text-main-dark-1"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        type="button"
      >
        ⋯
      </button>

      {isMenuOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsMenuOpen(false)} />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, transformOrigin: 'top right' }}
            animate={{ opacity: 1, scale: 1, transformOrigin: 'top right' }}
            exit={{ opacity: 0, scale: 0.95, transformOrigin: 'top right' }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute right-0 top-[32px] z-20 bg-surface-light dark:bg-surface-dark rounded-lg shadow-lg py-1 min-w-[120px]"
          >
            <button
              className="w-full px-4 py-2 text-left text-main-light-2 dark:text-main-dark-2 bg-backgroundElevated-light dark:bg-backgroundElevated-dark"
              onClick={() => {
                onOpen();
                setIsMenuOpen(false);
              }}
              type="button"
            >
              {t('leave')}
            </button>
          </motion.div>
        </>
      )}
    </div>
  );
}

// 메인 컴포넌트
export default function DMChatRoomPageV2(): JSX.Element {
  const { chat_room_id } = useParams<{ chat_room_id: string }>();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const messageAreaRef = useRef<HTMLDivElement>(null);

  const {
    messages,
    chatRoom,
    connectionStatus,
    messageStatuses,
    sendMessage,
    refetchMessages,
    hasMore,
    loadMore
  } = useChatRoom(chat_room_id!);

  const dmUserProfile = useMemo(() => {
    if (!chatRoom?.userProfiles) return undefined;
    const index = findIndex(chatRoom.userProfiles, (p: any) => p.userId !== chatRoom.currentUserId);
    return chatRoom.userProfiles[index];
  }, [chatRoom]);

  useEffect(() => {
    refreshScreenSize();
  }, []);

  useEffect(() => {
    if (messageAreaRef?.current) {
      messageAreaRef.current.scrollIntoView({ block: 'end' });
    }
  }, [messageAreaRef]);

  const onSentMessage = () => {
    refetchMessages();
    scrollToBottom(true);
  };

  const scrollToBottom = (_isSmooth = true) => {
    if (messageAreaRef?.current) {
      window.scrollTo({
        left: 0,
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  const onClickDialogButton = async () => {
    if (chatRoom?.chatRoomId) {
      const response = await leaveChatRoom(chatRoom.chatRoomId);
      if (response.success) {
        queryClient.invalidateQueries(['chatRooms']);
      }
    }
    window.history.back();
    setIsDialogOpen(false);
  };

  const noticeMessage = (): string | undefined => {
    if (chatRoom?.chatRoomUsers?.some((u: any) => !!u.deletedAt)) {
      return String(t('DMChatRoom.noticeDeletedUser'));
    }
    if (chatRoom?.chatRoomUsers?.some((u: any) => !!u.leftAt)) {
      return String(t('DMChatRoom.noticeLeftUser'));
    }
    return undefined;
  };

  return (
    <>
      <ChatRoomLayout
        header={
          <ChatRoomHeader
            chatRoom={chatRoom}
            connectionStatus={connectionStatus}
            onOpenMenu={() => setIsDialogOpen(true)}
          />
        }
        messages={
          <VirtualizedMessageList
            messages={messages}
            chatRoom={chatRoom}
            messageStatuses={messageStatuses}
            onLoadMore={loadMore}
            hasMore={hasMore}
            onNewMessage={onSentMessage}
          />
        }
        input={
          <ChatInput
            onSendMessage={sendMessage}
            disabled={!connectionStatus.isConnected || !isEmpty(noticeMessage())}
            dmUserProfile={dmUserProfile}
            messageAreaRef={messageAreaRef}
            onSentMessage={onSentMessage}
          />
        }
      />

      <LeaveChatRoomDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onClickDialogButton={onClickDialogButton}
      />
    </>
  );
}
